import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, Copy, Eye, EyeOff, Maximize2, Minimize2, PenLine, Trash2, X } from "lucide-react";
import { SECTION_META, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import type { InlineTarget } from "./inline-edit";
import { SectionPicker, sectionIcons } from "./outline";

/**
 * Phone/tablet pieces of the editor (below `lg`). Panels are bottom sheets that leave the page visible
 * above them; a selected section gets a compact action bar; text is typed in a bar docked above the
 * on-screen keyboard instead of an overlay that the keyboard would cover.
 */

export type SheetSize = "half" | "full";

/** Height of the fixed bottom nav (without the safe-area inset). Sheets and bars sit above it. */
export const NAV_H = "3.5rem";

/** Classes that turn a panel into a bottom sheet below `lg` (desktop layout is untouched). */
export function sheetClasses(open: boolean, size: SheetSize) {
  return cn(
    "max-lg:fixed max-lg:inset-x-0 max-lg:z-40 max-lg:w-full max-lg:rounded-t-[24px] max-lg:border-0 max-lg:shadow-[0_-16px_48px_-12px_rgb(15_23_42/0.35)]",
    "max-lg:bottom-[calc(3.5rem+env(safe-area-inset-bottom))] max-lg:overscroll-contain max-lg:transition-[height] max-lg:duration-200",
    size === "full" ? "max-lg:h-[calc(100dvh-7.5rem-env(safe-area-inset-bottom))]" : "max-lg:h-[55dvh]",
    open ? "max-lg:flex max-lg:animate-sheet-up" : "max-lg:hidden",
  );
}

/**
 * Grab bar + title for a mobile sheet. Drag the bar up to expand, down to shrink or close; the buttons do
 * the same for people who don't drag.
 */
export function SheetHandle({ title, size, onSize, onClose }: { title: string; size: SheetSize; onSize: (size: SheetSize) => void; onClose: () => void }) {
  const start = useRef<number | null>(null);
  const [dy, setDy] = useState(0);
  const end = () => {
    if (start.current === null) return;
    if (dy < -40) onSize("full");
    else if (dy > 60) {
      if (size === "full") onSize("half");
      else onClose();
    }
    start.current = null;
    setDy(0);
  };
  return (
    <div className="shrink-0 border-b border-line lg:hidden">
      <div
        className="flex touch-none justify-center pt-2 pb-1"
        onPointerDown={(e) => {
          start.current = e.clientY;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => start.current !== null && setDy(e.clientY - start.current)}
        onPointerUp={end}
        onPointerCancel={end}
        aria-hidden="true"
      >
        <span className="h-1.5 w-11 rounded-full bg-slate-300" />
      </div>
      <div className="flex items-center gap-1 px-3 pb-2">
        <p className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">{title}</p>
        <button
          type="button"
          className="grid size-10 place-items-center rounded-full text-slate-500 active:bg-slate-100"
          aria-label={size === "full" ? t.editor.mobile.shrink : t.editor.mobile.expand}
          onClick={() => onSize(size === "full" ? "half" : "full")}
        >
          {size === "full" ? <Minimize2 className="size-4" aria-hidden="true" /> : <Maximize2 className="size-4" aria-hidden="true" />}
        </button>
        <button type="button" className="grid size-10 place-items-center rounded-full text-slate-500 active:bg-slate-100" aria-label={t.editor.mobile.close} onClick={onClose}>
          <X className="size-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** The bottom sheet for adding a section: large tiles instead of the desktop popover list. */
export function AddSectionSheet({
  open,
  page,
  size,
  suggested,
  event,
  onSize,
  onClose,
  onAdd,
}: {
  open: boolean;
  page: WebsiteDefinition["pages"][number];
  size: SheetSize;
  suggested?: SectionType[];
  event?: boolean;
  onSize: (s: SheetSize) => void;
  onClose: () => void;
  onAdd: (type: SectionType) => void;
}) {
  return (
    <section className={cn("flex-col bg-white lg:hidden", sheetClasses(open, size))} aria-label={t.editor.picker.title}>
      <SheetHandle title={t.editor.picker.title} size={size} onSize={onSize} onClose={onClose} />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-2 py-3">
        <p className="px-2 pb-3 text-xs leading-relaxed text-slate-500">{t.editor.picker.subtitle}</p>
        <SectionPicker page={page} onAdd={onAdd} suggested={suggested} event={event} grid />
      </div>
    </section>
  );
}

/** Actions for the selected section, floating above the bottom nav. */
export function SectionActionBar({
  type,
  hidden,
  pinned,
  canUp,
  canDown,
  onUp,
  onDown,
  onDuplicate,
  onHide,
  onDelete,
  onEdit,
  onClose,
}: {
  type: SectionType;
  hidden: boolean;
  pinned: boolean;
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onDuplicate: () => void;
  onHide: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onClose: () => void;
}) {
  const Icon = sectionIcons[type];
  const label = SECTION_META[type].label;
  return (
    <div
      role="toolbar"
      aria-label={t.editor.toolbar.label}
      className="fixed inset-x-2 bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] z-40 mx-auto max-w-xl animate-sheet-up rounded-2xl bg-slate-900 p-1.5 text-white shadow-2xl lg:hidden"
    >
      <p className="sr-only" aria-live="polite">{t.editor.mobile.selected(label)}</p>
      <div className="flex items-center gap-1">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white/10">
          <Icon className="size-4 text-white/80" aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 truncate pl-1 text-sm font-bold">{label}</p>
        <button type="button" onClick={onEdit} className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white px-3.5 text-[13px] font-bold text-slate-900 active:bg-white/85">
          <PenLine className="size-3.5" aria-hidden="true" />
          {t.editor.mobile.editContent}
        </button>
        <BarButton label={t.common.close} onClick={onClose} className="size-9">
          <X aria-hidden="true" />
        </BarButton>
      </div>
      <div className="mt-1 grid grid-cols-5 border-t border-white/10 pt-1">
        <BarButton label={t.editor.toolbar.moveUp} disabled={pinned || !canUp} onClick={onUp} labelled>
          <ChevronUp aria-hidden="true" />
        </BarButton>
        <BarButton label={t.editor.toolbar.moveDown} disabled={pinned || !canDown} onClick={onDown} labelled>
          <ChevronDown aria-hidden="true" />
        </BarButton>
        <BarButton label={t.editor.toolbar.duplicate} disabled={pinned} onClick={onDuplicate} labelled>
          <Copy aria-hidden="true" />
        </BarButton>
        <BarButton label={hidden ? t.editor.toolbar.show : t.editor.toolbar.hide} onClick={onHide} labelled>
          {hidden ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
        </BarButton>
        <BarButton label={t.editor.toolbar.delete} disabled={pinned} onClick={onDelete} labelled>
          <Trash2 aria-hidden="true" />
        </BarButton>
      </div>
    </div>
  );
}

function BarButton({ label, labelled, disabled, onClick, className, children }: { label: string; labelled?: boolean; disabled?: boolean; onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={labelled ? undefined : label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex shrink-0 flex-col items-center justify-center rounded-xl text-white/85 active:bg-white/15 disabled:opacity-30 [&_svg]:size-[18px]",
        labelled ? "h-12 gap-0.5 text-[10px] font-semibold" : "size-11",
        className,
      )}
    >
      {children}
      {labelled ? <span className="max-w-full truncate px-0.5">{label}</span> : null}
    </button>
  );
}

/** Distance between the layout viewport's bottom and the visual viewport's (the on-screen keyboard). */
function useKeyboardInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setInset(Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)));
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return inset;
}

/**
 * Touch text editing: a bar docked above the keyboard. Same commit rules as the desktop overlay
 * (`InlineTextEditor`): every keystroke updates the draft; clearing a required field restores it.
 */
export function DockedTextEditor({
  target,
  sectionType,
  value,
  onChange,
  onDone,
  onNext,
}: {
  target: InlineTarget;
  sectionType: SectionType;
  value: string;
  onChange: (value: string | undefined) => void;
  onDone: () => void;
  onNext: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(value);
  const inset = useKeyboardInset();
  const { spec } = target;

  useLayoutEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.focus({ preventScroll: true });
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, []);

  // Grow with the text up to a third of the screen, then scroll.
  useLayoutEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, window.innerHeight / 3)}px`;
  }, [draft]);

  const emit = (text: string) => {
    setDraft(text);
    if (!text.trim()) onChange(spec.required ? target.initial : undefined);
    else onChange(text);
  };
  const finish = () => {
    if (!draft.trim() && spec.required) onChange(target.initial);
    onDone();
  };

  return (
    <div
      className="fixed inset-x-0 z-[60] border-t border-line bg-white px-3 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-12px_32px_-12px_rgb(15_23_42/0.3)]"
      style={{ bottom: inset }}
    >
      <div className="flex items-center gap-2 pb-1.5">
        <p className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-500">{t.editor.mobile.textTitle(SECTION_META[sectionType].label)}</p>
        <span className={cn("text-[11px] font-medium tabular-nums", draft.length >= spec.max ? "text-danger" : "text-slate-400")}>
          {draft.length}/{spec.max}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <textarea
          ref={ref}
          value={draft}
          maxLength={spec.max}
          rows={1}
          spellCheck
          enterKeyHint={spec.multiline ? "enter" : "done"}
          aria-label={t.editor.inline.label}
          className="min-h-11 flex-1 resize-none rounded-xl border border-line bg-slate-50 px-3 py-2.5 text-[16px] leading-snug text-slate-900 outline-none focus:border-primary focus:bg-white"
          onChange={(e) => emit(spec.multiline ? e.target.value : e.target.value.replace(/\s*\n\s*/g, " "))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !spec.multiline) {
              e.preventDefault();
              finish();
            }
          }}
        />
        <button
          type="button"
          aria-label={t.editor.mobile.next}
          className="grid size-11 shrink-0 place-items-center rounded-xl text-slate-500 ring-1 ring-line active:bg-slate-100"
          // Keep the keyboard open while moving to the next text.
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => {
            if (!draft.trim() && spec.required) onChange(target.initial);
            onNext();
          }}
        >
          <ChevronDown className="size-5" aria-hidden="true" />
        </button>
        <button type="button" className="h-11 shrink-0 rounded-xl bg-primary px-4 text-sm font-bold text-white active:opacity-90" onClick={finish}>
          {t.editor.mobile.done}
        </button>
      </div>
    </div>
  );
}

/** Compact autosave status for small screens; tapping it retries a failed save. */
export function SaveChip({ state, onRetry }: { state: "saved" | "saving" | "dirty" | "error"; onRetry: () => void }) {
  const text = { saved: t.editor.mobile.saved, saving: t.editor.mobile.saving, dirty: t.editor.mobile.unsaved, error: t.editor.mobile.failed }[state];
  const dot = { saved: "bg-emerald-500", saving: "bg-amber-400 animate-pulse", dirty: "bg-amber-500", error: "bg-danger" }[state];
  return (
    <button
      type="button"
      disabled={state !== "error"}
      onClick={onRetry}
      aria-live="polite"
      title={state === "error" ? t.editor.mobile.retry : undefined}
      className={cn(
        "flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold md:hidden",
        state === "error" ? "bg-danger-soft text-red-800" : "bg-slate-100 text-slate-600",
      )}
    >
      <span className={cn("size-1.5 rounded-full", dot)} aria-hidden="true" />
      {text}
    </button>
  );
}
